import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const load = () => {
  try {
    return JSON.parse(localStorage.getItem("capra_cart")) || [];
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(load);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("capra_cart", JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product, { size = null, color = null, quantity = 1 } = {}) => {
    setItems((prev) => {
      const key = `${product.id}|${size || ""}|${color || ""}`;
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, quantity: Math.min(10, i.quantity + quantity) } : i));
      }
      return [
        ...prev,
        {
          key,
          product_id: product.id,
          slug: product.slug,
          name: product.name,
          category: product.category,
          price: product.price,
          image: product.image || null,
          size,
          color,
          quantity,
        },
      ];
    });
    setDrawerOpen(true);
  }, []);

  const removeItem = useCallback((key) => setItems((prev) => prev.filter((i) => i.key !== key)), []);

  const updateQuantity = useCallback((key, quantity) => {
    setItems((prev) =>
      prev.map((i) => (i.key === key ? { ...i, quantity: Math.max(1, Math.min(10, quantity)) } : i))
    );
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);
  const subtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.quantity, 0), [items]);

  const actions = useMemo(
    () => ({ addItem, removeItem, updateQuantity, clearCart, setDrawerOpen }),
    [addItem, removeItem, updateQuantity, clearCart]
  );

  const value = useMemo(
    () => ({ items, count, subtotal, drawerOpen, ...actions }),
    [items, count, subtotal, drawerOpen, actions]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
